// Global Instructions Rule Applied!

import { ContactState, ContactsAction } from '../../interfaces/contacts.ts'
import {
  SET_FILTER_OPTIONS,
  SET_CONTACTS,
  SET_ACTIVE_FILTERS,
  SET_ACTIVE_SORTING,
  CLEAR_ACTIVE_FILTERS,
  CLEAR_ACTIVE_SORTING,
  UPDATE_CONTACT_RECORD,
  ADD_CONTACT,
  APPEND_CONTACTS,
  REMOVE_CONTACT_FROM_LIST
} from './constants.ts'
import { ContactRecordFields } from '../../data_models/contact_record.ts'
import { UPDATE_RECORD_BY_SEARCH } from '../../sync_engine/constants.ts'

const contactsReducer = (
  state: ContactState = {
    filterOptions: {
      data: {}
    },
    activeFilters: [],
    activeSorting: [],
    data: [],
    hasMore: false,
    currentPage: 1
  },
  action: ContactsAction
): ContactState => {
  switch (action.type) {
    case SET_FILTER_OPTIONS:
      const { response: filterOptionsResponse } = action.payload

      return {
        ...state,
        filterOptions: filterOptionsResponse
      }

    case SET_CONTACTS:
      const { response: contactsResponse } = action.payload

      return {
        ...state,
        data: contactsResponse.data,
        hasMore: contactsResponse.hasMore,
        currentPage: 1
      }

    case SET_ACTIVE_FILTERS:
      const { filters: activeFilters } = action.payload

      return {
        ...state,
        activeFilters
      }

    case SET_ACTIVE_SORTING:
      const { sorting: activeSorting } = action.payload

      return {
        ...state,
        activeSorting
      }

    case CLEAR_ACTIVE_FILTERS:
      return {
        ...state,
        activeFilters: []
      }

    case CLEAR_ACTIVE_SORTING:
      return {
        ...state,
        activeSorting: []
      }

    case UPDATE_CONTACT_RECORD:
      const { contactId, updates } = action.payload

      // Check if contact exists in state
      const contactExists = state.data.some((contact) => contact[ContactRecordFields.contacts__id] === contactId)

      // If contact doesn't exist, leave it be (don't add it)
      if (!contactExists) {
        return state
      }

      // Check if updates contains the contact ID field - indicates a full record refresh
      const updatesIdField = updates[ContactRecordFields.contacts__id] || updates.contacts__id || updates.id
      const updateFieldCount = Object.keys(updates).length
      const isFullRecordRefresh =
        (updatesIdField === contactId || updatesIdField === String(contactId)) && updateFieldCount > 5

      // If it's a full record refresh, fully replace the contact (not merge)
      // Otherwise, merge updates for backward compatibility with partial updates
      return {
        ...state,
        data: state.data.map((contact) => {
          if (contact[ContactRecordFields.contacts__id] === contactId) {
            return isFullRecordRefresh ? updates : { ...contact, ...updates }
          }
          return contact
        })
      }

    case ADD_CONTACT:
      const { contact: newContact } = action.payload

      return {
        ...state,
        data: [newContact, ...state.data]
      }

    case APPEND_CONTACTS:
      const { response: appendResponse } = action.payload

      return {
        ...state,
        data: [...state.data, ...appendResponse.data],
        hasMore: appendResponse.hasMore,
        currentPage: state.currentPage + 1
      }

    case REMOVE_CONTACT_FROM_LIST: {
      const { contactId: removedContactId } = action.payload
      const idStr = String(removedContactId)
      return {
        ...state,
        data: state.data.filter((contact) => String(contact[ContactRecordFields.contacts__id]) !== idStr)
      }
    }

    case UPDATE_RECORD_BY_SEARCH: {
      const { searchField, searchValue, updates } = action.payload

      return {
        ...state,
        data: state.data.map((record) => {
          const recordValue = record[searchField]
          const matches =
            recordValue === searchValue || String(recordValue) === String(searchValue) || recordValue == searchValue

          if (matches) {
            const filteredUpdates = Object.keys(updates).reduce(
              (acc, key) => {
                if (key in record) {
                  acc[key] = updates[key]
                }
                return acc
              },
              {} as Record<string, any>
            )

            if (Object.keys(filteredUpdates).length > 0) {
              return { ...record, ...filteredUpdates }
            }
          }
          return record
        })
      }
    }

    default:
      return state
  }
}

export default contactsReducer
export * from './contact_actions.ts'
export * from './selectors.ts'
